// Localização: frontend/src/components/AgendaTarefas/ModalCancelarTarefaRecorrente.jsx
// Modal para cancelamento/deleção de tarefas recorrentes com opções

import React, { useState } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import { AlertTriangle } from 'lucide-react';

const ModalCancelarTarefaRecorrente = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    tarefaInfo, 
    isLoading,
    isDeleting = false // Se true, é deleção; se false, é cancelamento
}) => {
    const [opcao, setOpcao] = useState('apenas_esta');
    const [motivo, setMotivo] = useState('');
    const [confirmarTodas, setConfirmarTodas] = useState(false);

    const handleConfirm = () => {
        if (!isDeleting && !motivo.trim()) {
            return; // Motivo é obrigatório apenas para cancelamento
        }
        onConfirm({ 
            opcao, 
            confirmar_todas: confirmarTodas,
            motivo: motivo.trim()
        });
    };

    const handleClose = () => {
        setOpcao('apenas_esta');
        setConfirmarTodas(false);
        setMotivo('');
        onClose();
    };

    const acaoTexto = isDeleting ? 'deletar' : 'cancelar';
    const acaoTextoCapitalizado = isDeleting ? 'Deletar' : 'Cancelar';

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`${acaoTextoCapitalizado} Tarefa Recorrente`}
            size="md"
        >
            <div className="space-y-6">
                {/* Informações da tarefa */}
                <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Tarefa Selecionada</h4>
                    <p className="text-sm text-gray-600">
                        {tarefaInfo?.descricao || tarefaInfo?.tipo_tarefa || 'Tarefa recorrente'}
                    </p>
                    {tarefaInfo?.hora_agendada && (
                        <p className="text-sm text-gray-500 mt-1">
                            Horário: {new Date(tarefaInfo.hora_agendada).toLocaleString('pt-BR')}
                        </p>
                    )}
                    {tarefaInfo?.regra_id && (
                        <p className="text-xs text-indigo-700 mt-1">
                            ID da Regra: {tarefaInfo.regra_id}
                        </p>
                    )}
                </div>

                {/* Campo de motivo (apenas para cancelamento) */}
                {!isDeleting && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Motivo do cancelamento <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Informe o motivo do cancelamento..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            rows={3}
                        />
                    </div>
                )}

                {/* Opções de cancelamento/deleção */}
                <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Escolha uma opção:
                    </label>

                    {/* Opção 1: Apenas esta */}
                    <div className="flex items-start">
                        <input
                            type="radio"
                            id="apenas_esta"
                            name="opcao_acao"
                            value="apenas_esta"
                            checked={opcao === 'apenas_esta'}
                            onChange={(e) => setOpcao(e.target.value)}
                            className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                        />
                        <label htmlFor="apenas_esta" className="ml-3 cursor-pointer">
                            <span className="block text-sm font-medium text-gray-900">
                                {acaoTextoCapitalizado} apenas esta tarefa
                            </span>
                            <span className="block text-xs text-gray-500 mt-1">
                                Apenas a tarefa selecionada será {acaoTexto === 'deletar' ? 'deletada' : 'cancelada'}. As demais permanecerão ativas.
                            </span>
                        </label>
                    </div>

                    {/* Opção 2: Todas as futuras */}
                    <div className="flex items-start">
                        <input
                            type="radio"
                            id="todas_futuras"
                            name="opcao_acao"
                            value="todas_futuras"
                            checked={opcao === 'todas_futuras'}
                            onChange={(e) => setOpcao(e.target.value)}
                            className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                        />
                        <label htmlFor="todas_futuras" className="ml-3 cursor-pointer">
                            <span className="block text-sm font-medium text-gray-900">
                                {acaoTextoCapitalizado} esta e todas as tarefas futuras desta regra
                            </span>
                            <span className="block text-xs text-gray-500 mt-1">
                                A tarefa selecionada e todas as tarefas futuras (pendentes) da mesma regra serão {acaoTexto === 'deletar' ? 'deletadas' : 'canceladas'}. Tarefas concluídas não serão afetadas.
                            </span>
                        </label>
                    </div>
                </div>

                {/* Confirmação adicional para "todas_futuras" */}
                {opcao === 'todas_futuras' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                            <div className="flex-1">
                                <h5 className="text-sm font-medium text-red-900 mb-2">
                                    Atenção: Esta ação afetará múltiplas tarefas
                                </h5>
                                <div className="flex items-center mt-3">
                                    <input
                                        type="checkbox"
                                        id="confirmar_todas"
                                        checked={confirmarTodas}
                                        onChange={(e) => setConfirmarTodas(e.target.checked)}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <label htmlFor="confirmar_todas" className="ml-2 text-sm text-red-900 cursor-pointer">
                                        Confirmo que desejo {acaoTexto} todas as tarefas futuras desta regra
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        disabled={isLoading}
                    >
                        Voltar
                    </button>
                    <PrimaryButton
                        onClick={handleConfirm}
                        disabled={
                            isLoading || 
                            (opcao === 'todas_futuras' && !confirmarTodas) ||
                            (!isDeleting && !motivo.trim())
                        }
                        className={`px-4 py-2 ${isDeleting ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                    >
                        {isLoading 
                            ? (isDeleting ? 'Deletando...' : 'Cancelando...') 
                            : `Confirmar ${acaoTextoCapitalizado}`
                        }
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    );
};

export default ModalCancelarTarefaRecorrente;

